import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ClipboardList, Factory, AlertTriangle, CheckCircle, 
  TrendingUp, TrendingDown, Package, Clock,
  ArrowRight, AlertCircle, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { dashboardService, customerReceivedNcService } from '../services/api';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = ['#3B82F6', '#F97316', '#EF4444', '#10B981', '#8B5CF6'];
const SEVERITY_COLORS = { minor: '#FBBF24', major: '#F97316', critical: '#EF4444' };
const NC_TYPE_COLORS = { internal: '#3B82F6', supplier: '#8B5CF6', customer: '#F97316' };

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [ncByType, setNcByType] = useState([]);
  const [ncBySeverity, setNcBySeverity] = useState([]);
  const [ncByMacroArea, setNcByMacroArea] = useState([]);
  const [lotsByStatus, setLotsByStatus] = useState([]);
  const [recentLots, setRecentLots] = useState([]);
  const [recentNcs, setRecentNcs] = useState([]);
  const [crncStats, setCrncStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [
        statsRes,
        ncTypeRes,
        ncSeverityRes,
        ncAreaRes,
        lotsStatusRes,
        recentLotsRes,
        recentNcsRes
      ] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getNcByType(),
        dashboardService.getNcBySeverity(),
        dashboardService.getNcByMacroArea(),
        dashboardService.getLotsByStatus(),
        dashboardService.getRecentLots(5),
        dashboardService.getRecentNcs(5),
      ]);

      setStats(statsRes.data);
      setNcByType(ncTypeRes.data);
      setNcBySeverity(ncSeverityRes.data);
      setNcByMacroArea(ncAreaRes.data);
      setLotsByStatus(lotsStatusRes.data);
      setRecentLots(recentLotsRes.data);
      setRecentNcs(recentNcsRes.data);

      // NC Cliente widget (silenzioso se l'endpoint non è raggiungibile)
      try {
        const crncRes = await customerReceivedNcService.dashboard();
        setCrncStats(crncRes.data?.totals || null);
      } catch { /* opzionale */ }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Caricamento cruscotto...</p>
        </div>
      </div>
    );
  }

  const kpiCards = [
    { title: 'Ordini Aperti', value: stats?.open_orders || 0, icon: ClipboardList, color: 'text-blue-600', bgColor: 'bg-blue-50', link: '/orders' },
    { title: 'Lotti in Corso', value: stats?.lots_in_progress || 0, icon: Factory, color: 'text-amber-600', bgColor: 'bg-amber-50', link: '/production-lots' },
    { title: 'Lotti in Ritardo', value: stats?.delayed_lots || 0, icon: Clock, color: 'text-rose-600', bgColor: 'bg-rose-50', link: '/production-lots' },
    { title: 'NC Aperte', value: stats?.open_ncs || 0, icon: AlertTriangle, color: 'text-orange-600', bgColor: 'bg-orange-50', link: '/non-conformities' },
  ];

  const qualityKpis = [
    { title: 'Resa al Primo Passaggio', value: `${stats?.first_pass_yield || 100}%`, icon: stats?.first_pass_yield >= 95 ? TrendingUp : TrendingDown, color: stats?.first_pass_yield >= 95 ? 'text-emerald-600' : 'text-rose-600', bgColor: stats?.first_pass_yield >= 95 ? 'bg-emerald-50' : 'bg-rose-50' },
    { title: 'Qtà Buona Totale', value: stats?.total_good_qty || 0, icon: CheckCircle, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
    { title: 'Qtà Scarti Totale', value: stats?.total_scrap_qty || 0, icon: AlertCircle, color: 'text-rose-600', bgColor: 'bg-rose-50' },
    { title: 'Costo Totale NC', value: `€${(stats?.nc_total_cost || 0).toLocaleString()}`, icon: TrendingDown, color: 'text-rose-600', bgColor: 'bg-rose-50' },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-amber-100 text-amber-700',
      closed: 'bg-slate-100 text-slate-700',
      suspended: 'bg-rose-100 text-rose-700',
      under_analysis: 'bg-purple-100 text-purple-700',
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Production KPIs */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Panoramica Produzione</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.map((kpi, index) => (
            <Link key={index} to={kpi.link}>
              <Card className="kpi-card hover:shadow-md transition-shadow cursor-pointer" data-testid={`kpi-${kpi.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">{kpi.title}</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">{kpi.value}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${kpi.bgColor}`}>
                      <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Quality KPIs */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Metriche Qualità</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {qualityKpis.map((kpi, index) => (
            <Card key={index} className="kpi-card" data-testid={`quality-kpi-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">{kpi.title}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{kpi.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${kpi.bgColor}`}>
                    <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* NC Cliente Ricevute widget */}
      {crncStats && (crncStats.total > 0) && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">NC Cliente Ricevute</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/customer-received-ncs" data-testid="crnc-widget-total">
              <Card className="kpi-card hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Totale NC Cliente</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">{crncStats.total}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-100"><AlertTriangle className="w-5 h-5 text-slate-600" /></div>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/customer-received-ncs?open_only=true" data-testid="crnc-widget-open">
              <Card className={`kpi-card hover:shadow-md transition-shadow cursor-pointer ${crncStats.open > 0 ? 'border-l-4 border-l-amber-500' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-700">NC Cliente Aperte</p>
                      <p className="text-3xl font-bold text-amber-700 mt-1">{crncStats.open}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-50"><Clock className="w-5 h-5 text-amber-600" /></div>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/customer-received-ncs?overdue_only=true" data-testid="crnc-widget-overdue">
              <Card className={`kpi-card hover:shadow-md transition-shadow cursor-pointer ${crncStats.overdue > 0 ? 'border-l-4 border-l-red-500' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm font-medium ${crncStats.overdue > 0 ? 'text-red-700' : 'text-slate-500'}`}>NC Cliente Scadute</p>
                      <p className={`text-3xl font-bold mt-1 ${crncStats.overdue > 0 ? 'text-red-700' : 'text-slate-900'}`}>{crncStats.overdue}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${crncStats.overdue > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
                      <AlertTriangle className={`w-5 h-5 ${crncStats.overdue > 0 ? 'text-red-600' : 'text-slate-500'}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/customer-received-ncs/dashboard" data-testid="crnc-widget-dashboard">
              <Card className="kpi-card hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-emerald-700">% Chiusura</p>
                      <p className="text-3xl font-bold text-emerald-700 mt-1">
                        {crncStats.total > 0 ? `${Math.round((crncStats.closed / crncStats.total) * 100)}%` : '—'}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-emerald-50"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      )}

      {/* NC Material Disposition */}
      {stats && stats.nc_total_nonconforming > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Disposizione Materiale NC</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Summary numbers */}
            <Card className="lg:col-span-1">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Pezzi NC Totali</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.nc_total_nonconforming}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-50">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <div className="space-y-2 pt-3 border-t border-slate-100">
                  {[
                    { label: 'Scartati', value: stats.nc_total_scrapped, color: '#EF4444' },
                    { label: 'Scarto Interno', value: stats.nc_total_internal_scrap, color: '#F97316' },
                    { label: 'Concessione Cliente', value: stats.nc_total_concession, color: '#8B5CF6' },
                    { label: 'Rilavorati', value: stats.nc_total_reworked, color: '#10B981' },
                  ].map(item => (
                    <div key={item.label} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
                        <span className="text-slate-600">{item.label}</span>
                      </div>
                      <span className="font-semibold text-slate-900 font-mono">{item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Distribuzione Disposizione</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={[
                      { name: 'Scarto', qty: stats.nc_total_scrapped, fill: '#EF4444' },
                      { name: 'Scarto Int.', qty: stats.nc_total_internal_scrap, fill: '#F97316' },
                      { name: 'Concessione', qty: stats.nc_total_concession, fill: '#8B5CF6' },
                      { name: 'Rilavorati', qty: stats.nc_total_reworked, fill: '#10B981' },
                    ]}
                    layout="vertical"
                    margin={{ left: 10, right: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={90} />
                    <Tooltip formatter={(value) => [`${value} pezzi`, 'Quantità']} />
                    <Bar dataKey="qty" radius={[0, 4, 4, 0]}>
                      {[
                        { fill: '#EF4444' },
                        { fill: '#F97316' },
                        { fill: '#8B5CF6' },
                        { fill: '#10B981' },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {/* Percentage bar */}
                <div className="mt-4">
                  <div className="flex h-4 rounded-full overflow-hidden bg-slate-100" data-testid="disposition-bar">
                    {[
                      { value: stats.nc_total_scrapped, color: '#EF4444' },
                      { value: stats.nc_total_internal_scrap, color: '#F97316' },
                      { value: stats.nc_total_concession, color: '#8B5CF6' },
                      { value: stats.nc_total_reworked, color: '#10B981' },
                    ].filter(s => s.value > 0).map((s, i) => (
                      <div
                        key={i}
                        style={{ width: `${(s.value / stats.nc_total_nonconforming) * 100}%`, backgroundColor: s.color }}
                        className="transition-all duration-500"
                        title={`${((s.value / stats.nc_total_nonconforming) * 100).toFixed(1)}%`}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-slate-400">
                    <span>0%</span>
                    <span>100% ({stats.nc_total_nonconforming} pezzi)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lots by Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Lotti per Stato</CardTitle>
          </CardHeader>
          <CardContent>
            {lotsByStatus.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={lotsByStatus}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="status" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-slate-400">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* NC by Type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Non Conformità per Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            {ncByType.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={ncByType}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="type"
                    label={({ type, count }) => `${type}: ${count}`}
                  >
                    {ncByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={NC_TYPE_COLORS[entry.type] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-slate-400">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Items Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Lots */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Lotti di Produzione Recenti</CardTitle>
            <Link to="/production-lots" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Vedi tutti <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {recentLots.length > 0 ? (
              <div className="space-y-3">
                {recentLots.map((lot) => (
                  <Link 
                    key={lot.id} 
                    to={`/production-lots/${lot.id}`}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div>
                      <p className="font-mono text-sm font-medium text-slate-900">{lot.lot_code}</p>
                      <p className="text-xs text-slate-500">{lot.product_code} - {lot.product_name}</p>
                    </div>
                    <Badge className={getStatusBadge(lot.status)}>
                      {lot.status?.replace('_', ' ')}
                    </Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                No recent lots
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent NCs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Non Conformità Recenti</CardTitle>
            <Link to="/non-conformities" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Vedi tutte <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {recentNcs.length > 0 ? (
              <div className="space-y-3">
                {recentNcs.map((nc) => (
                  <Link 
                    key={nc.id} 
                    to={`/non-conformities/${nc.id}`}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div>
                      <p className="font-mono text-sm font-medium text-slate-900">{nc.nc_number}</p>
                      <p className="text-xs text-slate-500 truncate max-w-[200px]">{nc.defect_description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`nc-${nc.nc_type}`}>
                        {nc.nc_type}
                      </Badge>
                      <Badge className={`severity-${nc.severity}`}>
                        {nc.severity}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                No recent non conformities
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* NC by Macro Area */}
      {ncByMacroArea.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Non Conformità per Macro Area</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={ncByMacroArea} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis dataKey="area" type="category" tick={{ fontSize: 12 }} width={100} />
                <Tooltip />
                <Bar dataKey="count" fill="#F97316" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
